<script setup lang="ts">
import { computed } from "vue";
import { EditPencil } from "@iconoir/vue";
import type { WorkspaceSubscription, SubscriptionStatus } from "@artech-global/types";
import { useFormat } from "~/composables/use-format";

defineOptions({ name: "SubscriptionCard" });

const props = defineProps<{
  subscription?: WorkspaceSubscription | null;
  canChange?: boolean;
}>();

const emit = defineEmits<{ (e: "change-plan"): void }>();

const { t } = useI18n();
const { d, c } = useFormat();

const has = computed(() => !!props.subscription && !!props.subscription.id);

const subState = computed(() => {
  const s = props.subscription;
  if (!s) return "unknown";
  if (s.cancelAtPeriodEnd && (s.status === "active" || s.status === "trialing")) return "cancelling";
  return s.status || "unknown";
});

const planLabel = computed(() => {
  // The status badge already says free/trialing — masking the plan name here
  // made an admin plan change look like it did nothing.
  if (props.subscription?.plan?.name) return props.subscription.plan.name;
  if (subState.value === "trialing") return t("workspace.subscription.freeTrial");
  if (subState.value === "free") return t("workspace.subscription.statusFree");
  return "—";
});

const periodRange = computed(() => {
  const s = props.subscription;
  if (!s?.periodStartTime || !s?.periodEndTime) return "";
  return `${d(s.periodStartTime, "long")} – ${d(s.periodEndTime, "long")}`;
});

const periodSubLabel = computed(() => {
  const s = props.subscription;
  if (subState.value === "trialing" && s?.trialEndTime) {
    return t("workspace.subscription.trialEndsOn", { date: d(s.trialEndTime, "long") });
  }
  if (subState.value === "cancelling" && s?.periodEndTime) {
    return t("workspace.subscription.accessUntil", { date: d(s.periodEndTime, "long") });
  }
  return "";
});

const billingPeriodLabel = computed(() => {
  const s = props.subscription;
  if (!s) return "—";
  if (s.status === "trialing" && s.periodStartTime && s.periodEndTime) {
    const days = Math.max(1, Math.round((s.periodEndTime - s.periodStartTime) / 86400));
    return t("workspace.subscription.daysCount", { count: days });
  }
  if (s.status === "free") return "—";
  if (!s.billingCycle) return "—";
  return t(`workspace.subscription.cycle.${s.billingCycle}`);
});

const pendingCycleNotice = computed(() => {
  const s = props.subscription;
  if (!s?.airwallexNextSubscriptionID || !s?.periodEndTime) return "";
  const cycle = s.billingCycle ? t(`workspace.subscription.cycle.${s.billingCycle}`) : "—";
  return t("workspace.subscription.cycleChangeScheduled", { cycle, date: d(s.periodEndTime, "long") });
});

function statusIntent(status?: SubscriptionStatus) {
  switch (status) {
    case "active":
      return "positive";
    case "trialing":
      return "informative";
    case "past-due":
      return "warning";
    case "cancelled":
      return "negative";
    case "free":
    default:
      return "neutral";
  }
}

function statusLabel(status?: SubscriptionStatus) {
  if (!status) return "—";
  switch (status) {
    case "free":
      return t("workspace.subscription.statusFree");
    case "trialing":
      return t("workspace.subscription.statusTrialing");
    case "active":
      return t("workspace.subscription.statusActive");
    case "past-due":
      return t("workspace.subscription.statusPastDue");
    case "cancelled":
      return t("workspace.subscription.statusCancelled");
    default:
      return status;
  }
}
</script>

<template>
  <SectionCard :title="t('workspace.subscription.title')">
    <template #actions>
      <ButtonIcon
        v-if="canChange"
        variant="neutral-flat"
        size="small"
        :title="t('workspace.subscription.changePlan')"
        :aria-label="t('workspace.subscription.changePlan')"
        @click="emit('change-plan')"
      >
        <EditPencil class="icon" />
      </ButtonIcon>
    </template>

    <div v-if="!has" class="text-body-small-tertiary content-neutral-medium">
      {{ t("workspace.subscription.empty") }}
    </div>

    <div v-else class="flex flex-col gap-small">
      <div class="flex flex-col gap-3xsmall md:flex-row md:gap-small">
        <span class="flex-1 text-body-secondary content-neutral-medium">{{ t("workspace.subscription.plan") }}</span>
        <div class="flex-1 flex flex-col gap-3xsmall">
          <div class="flex items-center gap-small flex-wrap">
            <span class="text-body-secondary">{{ planLabel }}</span>
            <Badge :intent="statusIntent(subscription?.status)" emphasis="medium" size="small">
              {{ statusLabel(subscription?.status) }}
            </Badge>
            <Badge v-if="subscription?.cancelAtPeriodEnd" intent="warning" emphasis="medium" size="small">
              {{ t("workspace.subscription.cancelAtPeriodEnd") }}
            </Badge>
          </div>
          <span v-if="subscription?.planPrice" class="text-body-small-tertiary">
            {{ c(subscription.planPrice, subscription.currency || "USD") }}
          </span>
        </div>
      </div>

      <div class="flex flex-col gap-3xsmall md:flex-row md:gap-small">
        <span class="flex-1 text-body-secondary content-neutral-medium">{{ t("workspace.subscription.currentPeriod") }}</span>
        <div class="flex-1 flex flex-col gap-3xsmall">
          <span class="text-body-secondary">{{ periodRange || '—' }}</span>
          <span v-if="periodSubLabel" class="text-body-small-tertiary">{{ periodSubLabel }}</span>
        </div>
      </div>

      <div class="flex flex-col gap-3xsmall md:flex-row md:gap-small">
        <span class="flex-1 text-body-secondary content-neutral-medium">{{ t("workspace.subscription.billingPeriod") }}</span>
        <div class="flex-1 flex flex-col gap-3xsmall">
          <span class="text-body-secondary">{{ billingPeriodLabel }}</span>
          <span v-if="pendingCycleNotice" class="text-body-small-tertiary">{{ pendingCycleNotice }}</span>
        </div>
      </div>
    </div>
  </SectionCard>
</template>
